'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { format, isToday, isYesterday, isSameDay } from 'date-fns';
import { DirectMessageWithSender } from '@/types/chat';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { Check, CheckCheck, ChevronDown, Loader2 } from 'lucide-react';

interface ChatWindowProps {
  messages: DirectMessageWithSender[];
  currentUserId: string;
  isLoading: boolean;
  hasMoreMessages: boolean;
  onLoadMore: (cursor: string) => Promise<void>;
  isMessageRead?: (messageCreatedAt: string) => boolean;
  className?: string;
}

type ListItem =
  | { type: 'date-separator'; date: Date; id: string }
  | { type: 'message'; message: DirectMessageWithSender; id: string };

function formatDateSeparator(date: Date): string {
  if (isToday(date)) return 'Today';
  if (isYesterday(date)) return 'Yesterday';
  return format(date, 'MMMM d, yyyy');
}

function formatMessageTime(dateString: string): string {
  try {
    return format(new Date(dateString), 'h:mm a');
  } catch {
    return '';
  }
}

function getInitials(name: string | null): string {
  if (!name) return '?';
  return name
    .split(' ')
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase()
    .substring(0, 2);
}

interface MessageItemProps {
  message: DirectMessageWithSender;
  isOwnMessage: boolean;
  showAvatar: boolean;
  isRead?: boolean;
}

function MessageItem({ message, isOwnMessage, showAvatar, isRead }: MessageItemProps) {
  const senderName = message.sender?.display_name || 'Unknown';
  const senderAvatar = message.sender?.avatar_url;

  return (
    <div
      data-testid="message-item"
      data-message-id={message.id}
      className={cn(
        'flex gap-2 px-4 py-1',
        isOwnMessage ? 'flex-row-reverse' : 'flex-row'
      )}
    >
      {/* Avatar placeholder for alignment */}
      <div className="w-8 shrink-0">
        {showAvatar && !isOwnMessage && (
          <Avatar className="jr-avatar-ring h-8 w-8">
            <AvatarImage src={senderAvatar || undefined} alt={senderName} />
            <AvatarFallback className="bg-[color:var(--jr-surface-muted)] text-xs text-[color:var(--jr-accent)]">
              {getInitials(senderName)}
            </AvatarFallback>
          </Avatar>
        )}
      </div>

      <div
        className={cn(
          'flex flex-col max-w-[70%]',
          isOwnMessage ? 'items-end' : 'items-start'
        )}
      >
        {/* Message bubble */}
        <div
          className={cn(
            'rounded-[22px] border px-4 py-2.5 break-words shadow-[0_10px_24px_rgba(0,0,0,0.18)]',
            isOwnMessage
              ? 'bg-[linear-gradient(180deg,rgba(106,39,54,0.98),rgba(79,28,40,0.98))] border-[color:rgba(214,168,139,0.18)] text-[#fff0e8]'
              : 'bg-[linear-gradient(180deg,rgba(55,35,32,0.95),rgba(43,27,25,0.98))] border-[color:rgba(214,168,139,0.16)] text-[color:var(--jr-text)]'
          )}
        >
          {/* Text content */}
          {message.content && (
            <p className="text-sm whitespace-pre-wrap">{message.content}</p>
          )}

          {/* Attachment */}
          {message.attachment_url && (
            <div className="mt-1">
              {message.attachment_type === 'image' && (
                <img
                  src={message.attachment_url}
                  alt="Attachment"
                  className="rounded-lg max-w-full max-h-64 object-contain"
                  loading="lazy"
                />
              )}
              {message.attachment_type === 'video' && (
                <video
                  src={message.attachment_url}
                  controls
                  className="rounded-lg max-w-full max-h-64"
                />
              )}
              {message.attachment_type === 'audio' && (
                <audio src={message.attachment_url} controls className="w-full" />
              )}
              {message.attachment_type === 'file' && (
                <a
                  href={message.attachment_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm underline"
                >
                  Download file
                </a>
              )}
            </div>
          )}
        </div>

        {/* Timestamp and read status */}
        <div className="flex items-center gap-1 mt-0.5 px-1">
          <span className="text-xs text-[color:var(--jr-text-muted)]">
            {formatMessageTime(message.created_at)}
          </span>
          {isOwnMessage && (
            <span
              data-testid="read-receipt-indicator"
              data-is-read={isRead}
              className={cn(
                'flex items-center',
                isRead ? 'text-[color:var(--jr-accent)]' : 'text-[color:var(--jr-text-muted)]'
              )}
            >
              {isRead ? (
                <CheckCheck className="h-3.5 w-3.5" aria-label="Read" />
              ) : (
                <Check className="h-3.5 w-3.5" aria-label="Sent" />
              )}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

interface DateSeparatorItemProps {
  date: Date;
}

function DateSeparatorItem({ date }: DateSeparatorItemProps) {
  return (
    <div
      data-testid="date-separator"
      className="flex items-center justify-center py-4"
    >
      <div className="rounded-full border border-[color:var(--jr-border)] bg-[color:rgba(49,31,29,0.76)] px-3 py-1 text-xs font-medium text-[color:var(--jr-text-soft)]">
        {formatDateSeparator(date)}
      </div>
    </div>
  );
}

function LoadingIndicator() {
  return (
    <div className="flex items-center justify-center py-4">
      <Loader2 className="h-5 w-5 animate-spin text-[color:var(--jr-text-muted)]" />
    </div>
  );
}

function MessageSkeleton() {
  return (
    <div className="flex gap-2 px-4 py-1">
      <Skeleton className="h-8 w-8 shrink-0 rounded-full bg-[color:rgba(72,48,44,0.92)]" />
      <div className="space-y-1">
        <Skeleton className="h-8 w-48 rounded-[22px] bg-[color:rgba(72,48,44,0.92)]" />
        <Skeleton className="h-3 w-12 bg-[color:rgba(72,48,44,0.76)]" />
      </div>
    </div>
  );
}

export function ChatWindow({
  messages,
  currentUserId,
  isLoading,
  hasMoreMessages,
  onLoadMore,
  isMessageRead,
  className,
}: ChatWindowProps) {
  const parentRef = useRef<HTMLDivElement>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const previousMessagesLengthRef = useRef(messages.length);
  const isInitialLoadRef = useRef(true);

  // Build list items with date separators
  const listItems = useMemo<ListItem[]>(() => {
    const items: ListItem[] = [];
    let lastDate: Date | null = null;

    for (const message of messages) {
      const messageDate = new Date(message.created_at);

      // Add date separator if this is a new day
      if (!lastDate || !isSameDay(lastDate, messageDate)) {
        items.push({
          type: 'date-separator',
          date: messageDate,
          id: `date-${format(messageDate, 'yyyy-MM-dd')}`,
        });
        lastDate = messageDate;
      }

      items.push({
        type: 'message',
        message,
        id: message.id,
      });
    }

    return items;
  }, [messages]);

  // Determine if we should show avatar for a message (first message or different sender)
  const shouldShowAvatar = useCallback(
    (index: number): boolean => {
      const item = listItems[index];
      if (item.type !== 'message') return false;

      // Look for previous message item
      for (let i = index - 1; i >= 0; i--) {
        const prevItem = listItems[i];
        if (prevItem.type === 'date-separator') {
          return true; // First message after date separator
        }
        if (prevItem.type === 'message') {
          return prevItem.message.sender_id !== item.message.sender_id;
        }
      }
      return true; // First message overall
    },
    [listItems]
  );

  // Virtual list configuration
  const virtualizer = useVirtualizer({
    count: listItems.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) => {
      const item = listItems[index];
      if (item.type === 'date-separator') return 48;
      // Estimate based on content length
      const contentLength = item.message.content?.length || 0;
      const hasAttachment = !!item.message.attachment_url;
      const baseHeight = 60;
      const textHeight = Math.ceil(contentLength / 40) * 20;
      const attachmentHeight = hasAttachment ? 200 : 0;
      return baseHeight + textHeight + attachmentHeight;
    },
    overscan: 5,
  });

  // Scroll to bottom
  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    if (parentRef.current) {
      parentRef.current.scrollTo({
        top: parentRef.current.scrollHeight,
        behavior,
      });
    }
  }, []);

  // Load more messages when scrolling to top
  const handleScroll = useCallback(async () => {
    const container = parentRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;

    // Check if we're at the bottom
    const atBottom = scrollHeight - scrollTop - clientHeight < 50;
    setIsAtBottom(atBottom);
    setShowScrollButton(!atBottom && scrollTop > 200);

    // Load more when near top
    if (scrollTop < 100 && hasMoreMessages && !isLoadingMore && messages.length > 0) {
      setIsLoadingMore(true);
      const oldestMessage = messages[0];
      const previousScrollHeight = container.scrollHeight;

      try {
        await onLoadMore(oldestMessage.created_at);
        // Maintain scroll position after loading more messages
        requestAnimationFrame(() => {
          if (container) {
            const newScrollHeight = container.scrollHeight;
            container.scrollTop = newScrollHeight - previousScrollHeight;
          }
        });
      } finally {
        setIsLoadingMore(false);
      }
    }
  }, [hasMoreMessages, isLoadingMore, messages, onLoadMore]);

  // Auto-scroll to bottom when new messages arrive (if already at bottom)
  useEffect(() => {
    const prevLength = previousMessagesLengthRef.current;
    const currentLength = messages.length;
    previousMessagesLengthRef.current = currentLength;

    // Skip if this is the initial load or no new messages
    if (currentLength <= prevLength) return;

    // Check if the new message is from the current user or if we're at bottom
    const newMessage = messages[currentLength - 1];
    const isOwnMessage = newMessage?.sender_id === currentUserId;

    if (isOwnMessage || isAtBottom) {
      // Use instant scroll for own messages, smooth for others
      requestAnimationFrame(() => {
        scrollToBottom(isOwnMessage ? 'instant' : 'smooth');
      });
    }
  }, [messages, currentUserId, isAtBottom, scrollToBottom]);

  // Initial scroll to bottom on first load
  useEffect(() => {
    if (!isLoading && messages.length > 0 && isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      requestAnimationFrame(() => {
        scrollToBottom('instant');
      });
    }
  }, [isLoading, messages.length, scrollToBottom]);

  // Loading state
  if (isLoading && messages.length === 0) {
    return (
      <div
        className={cn('flex h-full flex-col bg-[color:rgba(17,10,10,0.18)]', className)}
        data-testid="chat-window-loading"
      >
        <div className="flex-1 overflow-hidden p-2">
          {[...Array(8)].map((_, i) => (
            <MessageSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  // Empty state
  if (messages.length === 0) {
    return (
      <div
        className={cn(
          'flex h-full flex-col items-center justify-center bg-[color:rgba(17,10,10,0.18)]',
          className
        )}
        data-testid="chat-window-empty"
      >
        <p className="text-sm text-[color:var(--jr-text-soft)]">No messages yet</p>
        <p className="mt-1 text-xs text-[color:var(--jr-text-muted)]">
          Send a message to start the conversation
        </p>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'relative flex h-full flex-col bg-[radial-gradient(circle_at_top,rgba(106,39,54,0.08),transparent_26%),linear-gradient(180deg,rgba(24,15,14,0.82),rgba(18,11,11,0.96))]',
        className
      )}
      data-testid="chat-window"
    >
      {/* Messages container with virtual scrolling */}
      <div
        ref={parentRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto"
        data-testid="chat-window-scroll-container"
      >
        {/* Loading more indicator */}
        {isLoadingMore && <LoadingIndicator />}

        {/* Virtual list */}
        <div
          style={{
            height: `${virtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {virtualizer.getVirtualItems().map((virtualRow) => {
            const item = listItems[virtualRow.index];

            return (
              <div
                key={item.id}
                data-index={virtualRow.index}
                ref={virtualizer.measureElement}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                {item.type === 'date-separator' ? (
                  <DateSeparatorItem date={item.date} />
                ) : (
                  <MessageItem
                    message={item.message}
                    isOwnMessage={item.message.sender_id === currentUserId}
                    showAvatar={shouldShowAvatar(virtualRow.index)}
                    isRead={isMessageRead?.(item.message.created_at)}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Scroll to bottom button */}
      {showScrollButton && (
        <Button
          variant="secondary"
          size="icon"
          className="absolute right-4 bottom-4 z-10 rounded-full border border-[color:var(--jr-border)] bg-[color:rgba(49,31,29,0.94)] text-[color:var(--jr-text)] shadow-lg hover:bg-[color:rgba(66,42,38,0.98)]"
          onClick={() => scrollToBottom('smooth')}
          data-testid="scroll-to-bottom-button"
        >
          <ChevronDown className="h-5 w-5" />
        </Button>
      )}
    </div>
  );
}

export type { ChatWindowProps };
